import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';

export interface GroupMemberRow {
  qbo_customer_id: number;
  name: string;
  enabled: boolean;
  orders_customer_id: string | null;
  orders_active: boolean | null;
}

export interface GroupMemberOf {
  master_customer_id: string;
  master_name: string;
  group_kind: 'chain' | 'franchise';
  group_brand: string | null;
  billing_mode: 'master' | 'independent' | null;
  /** Field names this store manages locally — master syncs skip them. */
  overrides: string[];
}

export interface CustomerGroupInfo {
  group_kind: 'chain' | 'franchise' | null;
  group_brand: string | null;
  /** 'master' = master's accounting + payment settings sync onto every member store. */
  group_billing_mode: 'master' | 'independent' | null;
  /** Set when THIS customer is a member store of a group master. */
  member_of: GroupMemberOf | null;
  /** This account's QBO customer has sub-customers (candidates for a group). */
  is_qbo_parent: boolean;
  master_user_count: number;
  members: GroupMemberRow[];
}

export interface AttachUsersResult {
  stores: number;
  results: { store: string; attached: number; reactivated: number; already: number }[];
}

/** Chain/franchise group state for one admin customer detail page. */
export function useCustomerGroup(customerId: string | undefined) {
  return useQuery({
    queryKey: ['admin-customer-group', customerId],
    queryFn: () => adminFetch<CustomerGroupInfo>(`admin-customer-group?customer_id=${customerId}`),
    enabled: !!customerId,
    staleTime: 30_000,
  });
}

export function useSetCustomerGroup(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { group_kind: 'chain' | 'franchise' | null; group_brand?: string | null }) =>
      adminFetch('admin-customer-group', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'set_group', ...input }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-customer-group', customerId] }),
  });
}

export function useSetGroupBillingMode(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: 'master' | 'independent') =>
      adminFetch<{ billing_mode: string; stores_synced: number }>('admin-customer-group', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'set_billing_mode', billing_mode: mode }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-customer-group', customerId] }),
  });
}

/** "Reset to master" on a member store: clear overrides + re-sync from the master. */
export function useResetGroupOverrides(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      adminFetch('admin-customer-group', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'reset_overrides' }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-customer-group', customerId] });
      // The store's billing/payment values changed — refresh everything on the page.
      void qc.invalidateQueries();
    },
  });
}

export interface EnableGroupStoresResult {
  master: { id: string; name: string; billing_mode?: string | null };
  stores_enabled: { qbo_customer_id: number; name: string; customer_id: string }[];
  skipped: { qbo_customer_id: number; name: string; reason: string }[];
  failures: { qbo_customer_id: number; name: string; error: string }[];
  remaining: number;
}

/**
 * "Enable all stores" — one call to the time-budgeted sweep. The endpoint
 * is resumable via `remaining`; the ChainGroupCard button loops until 0.
 */
export function useEnableGroupStores(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      adminFetch<EnableGroupStoresResult>('admin-enable-group-stores', {
        method: 'POST',
        body: JSON.stringify({ master_customer_id: customerId }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-customer-group', customerId] }),
  });
}

export function useAttachGroupUsers(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      adminFetch<AttachUsersResult>('admin-customer-group', {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, action: 'attach_users' }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-customer-group', customerId] }),
  });
}
